import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays } from "lucide-react";
import Layout, { Breadcrumbs } from "../components/Layout";
import Seo from "../components/Seo";
import { Calendar } from "../components/ui/calendar";
import { getResultByDate } from "../lib/api";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Teer Calendar", path: "/teer-calendar/" },
];

const START = new Date(2020, 0, 4);

function toSort(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function ResultPanel({ selected }) {
  const dateSort = selected ? toSort(selected) : null;
  const { data, isFetching } = useQuery({
    queryKey: ["by-date", dateSort],
    queryFn: () => getResultByDate(dateSort),
    enabled: !!dateSort,
  });

  if (!selected) {
    return (
      <div
        data-testid="calendar-hint"
        className="flex h-full flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500"
      >
        <CalendarDays size={30} className="mb-2 text-[#1ab5a3]" />
        Select any date from the calendar to see that day's Bhutan Morning Teer
        result.
      </div>
    );
  }

  return (
    <div
      data-testid="calendar-result"
      className="rounded-xl border-2 border-[#1ab5a3] bg-white p-4 shadow-sm"
    >
      <p className="text-center text-lg font-extrabold text-[#10161f]">
        {data?.result?.date || (data && data.date) || ""}
      </p>

      {isFetching ? (
        <p className="mt-4 text-center text-sm text-slate-500">Loading…</p>
      ) : data?.available ? (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-slate-50 py-3 text-center">
            <div className="text-xs font-semibold text-slate-500">F/R (10:30 AM)</div>
            <div className="mt-1 text-3xl font-extrabold text-[#10161f]" data-testid="cal-fr">
              {data.result.fr}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 py-3 text-center">
            <div className="text-xs font-semibold text-slate-500">S/R (11:30 AM)</div>
            <div className="mt-1 text-3xl font-extrabold text-[#10161f]" data-testid="cal-sr">
              {data.result.sr}
            </div>
          </div>
        </div>
      ) : (
        <p
          data-testid="cal-unavailable"
          className="mt-4 text-center text-sm text-slate-500"
        >
          {data?.reason === "future"
            ? "This date is in the future — result not declared yet."
            : data?.reason === "pending"
            ? "Today's result is not declared yet. Please check back after the draw time."
            : "No result available for this date."}
        </p>
      )}
    </div>
  );
}

export default function TeerCalendar() {
  const today = new Date();
  const [selected, setSelected] = useState(today);

  return (
    <Layout>
      <Seo
        title="Bhutan Morning Teer Calendar – Result by Date"
        description="Bhutan Morning Teer calendar. Pick any date to instantly see that day's F/R and S/R result. Draw timings: 10:30 AM and 11:30 AM."
        path="/teer-calendar/"
        breadcrumb={crumbs}
      />
      <Breadcrumbs items={crumbs} />

      <div className="mx-auto max-w-3xl px-4 py-4">
        <h1 className="text-2xl font-extrabold text-[#10161f] sm:text-3xl">
          Bhutan Morning Teer Calendar
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Tap any date to instantly see that day's Bhutan Morning Teer result
          (F/R & S/R).
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div
            data-testid="teer-calendar"
            className="flex justify-center rounded-xl border border-slate-200 bg-white p-2 shadow-sm"
          >
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => d && setSelected(d)}
              disabled={{ before: START, after: today }}
              defaultMonth={selected}
            />
          </div>
          <ResultPanel selected={selected} />
        </div>

        <p className="mt-5 text-xs text-slate-500">
          Results are available from 04-01-2020 up to today. Future dates are not
          declared yet.
        </p>
      </div>
    </Layout>
  );
}
